"use client";

import type * as React from "react";
import { useCallback, useRef, useState } from "react";

import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

const TRANSITION = { duration: 0.25, ease: "easeOut" as const };

export interface FormInlineWrapperProps {
  renderForm: (props: { onSuccess: () => void }) => React.ReactNode;
  successContent: React.ReactNode | ((props: { onReset: () => void }) => React.ReactNode);
}

export function FormInlineWrapper({ renderForm, successContent }: FormInlineWrapperProps) {
  const [succeeded, setSucceeded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSuccess = useCallback(() => {
    setSucceeded(true);
    requestAnimationFrame(() => {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const handleReset = useCallback(() => {
    setSucceeded(false);
  }, []);

  return (
    <div ref={containerRef}>
      <AnimatePresence mode='wait'>
        {!succeeded ? (
          <m.div key='form' exit={{ opacity: 0, y: -8 }} transition={TRANSITION}>
            {renderForm({ onSuccess: handleSuccess })}
          </m.div>
        ) : (
          <m.div key='success' initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={TRANSITION}>
            {typeof successContent === "function" ? successContent({ onReset: handleReset }) : successContent}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
